import React, {useMemo, useState} from "react";
import SearchInput from "./SearchInput";
import PluginSection from "./PluginSection";
import ToggleRow from "./ToggleRow";

const OTHER_PLUGIN = "Other";

function pluginOf(permission: string): string {
    const idx = permission.indexOf(".");
    return idx > 0 ? permission.substring(0, idx) : OTHER_PLUGIN;
}

function labelOf(permission: string): string {
    const idx = permission.indexOf(".");
    return idx > 0 ? permission.substring(idx + 1) : permission;
}

export default function PermissionList(props: {
    allPermissions: string[],
    granted: Set<string>,
    inherited?: Map<string, string[]>,
    onToggle: (permission: string, currentlyDirect: boolean) => void
}) {
    const {allPermissions, granted, inherited, onToggle} = props;

    const [search, setSearch] = useState("");

    // Auto-expand plugins that have something granted or inherited (computed once on mount).
    const [expanded, setExpanded] = useState<Set<string>>(() => {
        const s = new Set<string>();
        for (const perm of allPermissions) {
            if (granted.has(perm) || (inherited?.get(perm)?.length ?? 0) > 0) {
                s.add(pluginOf(perm));
            }
        }
        return s;
    });

    // Group permissions by plugin, sorted; "Other" last.
    const plugins = useMemo(() => {
        const map = new Map<string, string[]>();
        for (const perm of allPermissions) {
            const plugin = pluginOf(perm);
            if (!map.has(plugin)) {
                map.set(plugin, []);
            }
            map.get(plugin)!.push(perm);
        }

        const entries = Array.from(map.entries());
        entries.sort((a, b) => {
            if (a[0] === OTHER_PLUGIN) return 1;
            if (b[0] === OTHER_PLUGIN) return -1;
            return a[0].localeCompare(b[0]);
        });
        for (const [, perms] of entries) {
            perms.sort((a, b) => a.localeCompare(b));
        }
        return entries;
    }, [allPermissions]);

    const query = search.trim().toLowerCase();

    const toggleOpen = (plugin: string) => {
        setExpanded(prev => {
            const next = new Set(prev);
            if (next.has(plugin)) {
                next.delete(plugin);
            } else {
                next.add(plugin);
            }
            return next;
        });
    };

    const visiblePlugins = plugins
        .map(([plugin, perms]) => ({
            plugin,
            perms,
            visible: query ? perms.filter(p => p.toLowerCase().includes(query)) : perms
        }))
        .filter(p => p.visible.length > 0);

    return (
        <div className="space-y-3">
            <SearchInput value={search} onChange={setSearch} placeholder="Search permissions..."/>

            <div className="space-y-2">
                {visiblePlugins.map(({plugin, perms, visible}) => {
                    const grantedCount = perms.filter(p => granted.has(p)).length;
                    const open = query !== "" || expanded.has(plugin);

                    return (
                        <PluginSection
                            key={plugin}
                            plugin={plugin}
                            grantedCount={grantedCount}
                            totalCount={perms.length}
                            open={open}
                            onToggleOpen={() => toggleOpen(plugin)}>
                            {visible.map(perm => {
                                const isDirect = granted.has(perm);
                                const groups = inherited?.get(perm) ?? [];
                                const isInherited = groups.length > 0;
                                return (
                                    <ToggleRow
                                        key={perm}
                                        label={labelOf(perm)}
                                        title={perm}
                                        active={isDirect}
                                        outlined={!isDirect && isInherited}
                                        onClick={() => onToggle(perm, isDirect)}
                                        badge={isInherited ? groups.map(g => (
                                            <span key={g}
                                                  className="text-[10px] rounded px-1.5 py-0.5 bg-green-500/15 text-green-300 border border-green-500/30 whitespace-nowrap">
                                                inherited from {g}
                                            </span>
                                        )) : undefined}
                                    />
                                );
                            })}
                        </PluginSection>
                    );
                })}
                {visiblePlugins.length === 0 && (
                    <p className="text-neutral-500 text-sm text-center py-6">No permissions match your search.</p>
                )}
            </div>
        </div>
    )
}
